const { debounce, getElement, fetchJSON } = window.BAO.Utils

class FormValidation extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.formDisabled = false
    this.restoreInputValues()
    this.checkRequiredState()

    this.setupCountryCode()
    this.handlePhoneInput()
  }

  setupListeners () {
    super.setupListeners()

    this.validateAndPreserveInput = this.validateAndPreserveInput.bind(this)

    if (this.itiValidationField) {
      this.handlePhoneInput = this.handlePhoneInput.bind(this)
      this.listeners.add(
        this.itiValidationField,
        'input',
        this.handlePhoneInput,
      )
      this.listeners.add(
        this.itiValidationField,
        'countrychange',
        this.handlePhoneInput,
      )
    }

    if (this?.els?.required?.exists && this?.els?.submit?.exists) {
      this.els.required.elements.forEach(requiredEl => {
        requiredEl.addEventListener(
          'input',
          debounce(this.validateAndPreserveInput, 750),
        )
      })
    }

    this.handleMaidenNameInput = this.handleMaidenNameInput.bind(this)
    if (this?.els?.maidenNameInput?.exists) {
      this?.els?.maidenNameInput?.elements.forEach(input => {
        input.addEventListener('input', this.handleMaidenNameInput)
      })
    }

    this.handleSubmitClick = this.handleSubmitClick.bind(this)
    if (this?.els?.submit?.exists) {
      this.els.submit.element.addEventListener('click', this.handleSubmitClick)
    }
  }

  handleSubmitClick (e) {
    const target = e.target.closest(this.els.submit.selector)

    if (target.getAttribute('aria-disabled') === 'true') {
      e.preventDefault()
    }

    if (this.dataset.errorsEnabled === 'true') {
      this.handleErrors()
    }
  }

  handleMaidenNameInput (e) {
    const inputValue = e.target?.value
    if (inputValue && inputValue.length > 0) {
      this.disableForm()
    }
  }

  disableForm () {
    this.formDisabled = true

    // Disable submit
    if (this?.els?.submit?.exists) {
      this.els.submit.element.ariaDisabled = true

      if (this.dataset.errorsEnabled !== 'true') {
        this.els.submit.element.disabled = true
      }
    }

    // Disable form and prevent submit event
    const form = getElement('form', {
      context: this,
    })
    if (form) {
      form.setAttribute('disabled', 'disabled')
      form.addEventListener('submit', e => {
        e.preventDefault()
      })
    }
  }

  validateAndPreserveInput (input) {
    if (this.formDisabled) return

    this.validateInput(input)
    this.preserveInputValues(input.target)
    this.checkRequiredState()
  }

  validateInput (input) {
    const form = input.target.form

    // We only want to run our own validation if the form has a novalidate attribute.
    if (!form.noValidate) return

    const { target: inputField } = input

    if (inputField.type === 'email') {
      if (!inputField.validity.valid) {
        inputField.setAttribute('aria-invalid', true)
        inputField.setAttribute(
          'aria-errormessage',
          window.theme.strings.invalidEmail,
        )
      } else {
        inputField.removeAttribute('aria-invalid')
        inputField.removeAttribute('aria-errormessage')
      }

      inputField.reportValidity()
      return
    }

    if (inputField.type === 'tel') {
      let validNumber
      if (this.iti && typeof this.iti.isValidNumber === 'function') {
        validNumber = this.iti.isValidNumber()
      } else {
        validNumber = inputField.validity.valid
      }

      if (!validNumber) {
        inputField.setAttribute('aria-invalid', true)
        inputField.setAttribute(
          'aria-errormessage',
          window.theme.strings.invalidEmail,
        )
      } else {
        inputField.removeAttribute('aria-invalid')
        inputField.removeAttribute('aria-errormessage')
      }

      inputField.reportValidity()
      return
    }

    if (inputField.type === 'date') {
      const value = inputField.value
      if (!value) return

      const min = inputField.dataset.min
      const max = inputField.dataset.max

      const valueDate = new Date(value)
      const minDate = min ? new Date(min) : null
      const maxDate = max ? new Date(max) : null

      let isValid = true

      if (minDate && valueDate < minDate) isValid = false
      if (maxDate && valueDate > maxDate) isValid = false

      if (!isValid) {
        inputField.setAttribute('aria-invalid', true)
        inputField.setAttribute(
          'aria-errormessage',
          window.theme.strings.invalidDate,
        )
      } else {
        inputField.removeAttribute('aria-invalid')
        inputField.removeAttribute('aria-errormessage')
      }

      inputField.reportValidity()
    }
  }

  handleErrors () {
    const errorEls = this?.els?.error?.elements || []
    const requiredInputs = this?.els?.required?.elements || []

    errorEls.forEach(error => {
      const inputName = error.dataset.inputName
      if (!inputName) return

      const matchingRequiredEls = [...requiredInputs].filter(
        el => el.dataset.inputName === inputName && !el.disabled,
      )

      if (matchingRequiredEls.length === 0) return

      let isInvalid

      if (matchingRequiredEls[0].type === 'radio') {
        isInvalid = !this.validateRadioGroup(matchingRequiredEls)
      } else {
        isInvalid = matchingRequiredEls.some(input => {
          switch (input.type) {
            case 'text':
            case 'password':
            case 'textarea':
              return !this.validateTextInput(input)

            case 'email':
              return !this.validateEmailInput(input)

            case 'checkbox':
              return !this.validateCheckbox(input)

            case 'tel':
              return !this.validateTelInput(input)

            case 'date':
              return !this.validateDateInput(input)

            default:
              return !this.validateTextInput(input)
          }
        })
      }

      error.setAttribute('aria-hidden', !isInvalid)

      matchingRequiredEls.forEach(input => {
        const fieldWrapper = input.closest('.frm-Form_Field')
        if (fieldWrapper) {
          fieldWrapper.classList.toggle('frm-Form_Field-error', isInvalid)
        }
      })
    })
  }

  checkRequiredState () {
    if (!this?.els?.required?.exists) return

    let allAreFilled = true
    const handledRadioGroups = new Set()

    for (const input of this.els.required.elements) {
      if (input.disabled) continue

      switch (input.type) {
        case 'text':
        case 'password':
        case 'textarea':
          if (!this.validateTextInput(input)) allAreFilled = false
          break

        case 'email':
          if (!this.validateEmailInput(input)) allAreFilled = false
          break

        case 'checkbox':
          if (!this.validateCheckbox(input)) allAreFilled = false
          break

        case 'radio': {
          if (handledRadioGroups.has(input.name)) break

          const group = Array.from(this.els.required.elements).filter(
            el => el.type === 'radio' && el.name === input.name && !el.disabled,
          )

          if (!this.validateRadioGroup(group)) allAreFilled = false
          handledRadioGroups.add(input.name)
          break
        }

        case 'tel':
          if (!this.validateTelInput(input)) allAreFilled = false
          break

        default:
          if (!this.validateTextInput(input)) allAreFilled = false
      }
    }

    if (this?.els?.submit?.exists) {
      this.els.submit.element.ariaDisabled = !allAreFilled

      if (this.dataset.errorsEnabled !== 'true') {
        this.els.submit.element.disabled = !allAreFilled
      }
    }
  }

  validateTextInput (input) {
    return !input.disabled && input.value.length > 0
  }

  validateEmailInput (input) {
    return !input.disabled && input.value.length > 0 && input.validity.valid
  }

  validateCheckbox (input) {
    return !input.disabled && input.checked
  }

  validateTelInput (input) {
    if (input.disabled) return false

    if (input.value.trim() === '') return true

    return (
      !input.hasAttribute('data-iti-validation') ||
      this.iti?.isValidNumber() === true
    )
  }

  validateDateInput (input) {
    if (input.disabled || input.ariaInvalid) return false

    if (input.value.trim() === '') return false

    const min = input.dataset.min
    const max = input.dataset.max

    const valueDate = new Date(input.value)
    const minDate = min ? new Date(min) : null
    const maxDate = max ? new Date(max) : null

    if (minDate && valueDate < minDate) return false
    if (maxDate && valueDate > maxDate) return false

    return true
  }

  validateRadioGroup (inputs) {
    const enabledInputs = inputs.filter(i => !i.disabled)
    return enabledInputs.some(i => i.checked)
  }

  preserveInputValues (element) {
    // Save the input value to the session storage.
    if (element.type === 'text' || element.type === 'email') {
      window.sessionStorage.setItem(`BAO-${element.name}`, element.value)
    }
  }

  restoreInputValues () {
    // Restore the input value from the session storage.
    if (this?.els?.required?.exists) {
      this.els.required.elements.forEach(requiredEl => {
        // Only restore if the input is invalid.
        if (requiredEl.validity.valid) {
          // Wipe the stored value.
          window.sessionStorage.removeItem(`BAO-${requiredEl.name}`)

          return
        }

        if (requiredEl.type === 'text' || requiredEl.type === 'email') {
          const value = window.sessionStorage.getItem(`BAO-${requiredEl.name}`)
          if (value) {
            requiredEl.value = value
          }
        }
      })
    }
  }

  setupCountryCode () {
    if (this.itiValidationField) {
      this.iti = window.intlTelInput(this.itiValidationField, {
        utilsScript:
          'https://cdn.jsdelivr.net/npm/intl-tel-input@18.2.1/build/js/utils.js',
        separateDialCode: true,
        showFlags: false,
        initialCountry: 'auto',
        strictMode: true,
        async geoIpLookup (callback) {
          const response = await fetchJSON(
            `/browsing_context_suggestions.json`,
            {
              method: 'GET',
            },
          )

          callback(response.detected_values.country.handle)
        },
      })
    }
  }

  async handlePhoneInput () {
    if (this.els['register-phone-formatted']) {
      const value =
        (await this.iti?.getNumber()) ||
        (this.itiValidationField ? this.itiValidationField.value : '')

      this.els['register-phone-formatted'].element.value = value

      const phoneLabel = this.els['phone-label']?.element

      if (!phoneLabel) return

      if (!value || value === '') {
        phoneLabel.classList.remove('frm-Form_Label-show')
      } else {
        phoneLabel.classList.add('frm-Form_Label-show')
      }
    }
  }

  get itiValidationField () {
    return getElement('[data-iti-validation]', { context: this })
  }
}

customElements.define('form-validation', FormValidation)
